// openedAtSnapshot.js
//
// Gathers the "before" state of the elements a user was looking at
// when they created an issue. Sent to POST /issue as
// { sourceViewId, openedAtSnapshotElements } and stored on the Issue
// document — see back/models/issue.js openedAtSnapshot field.
//
// Consumed by GET /issue/:id/candidate-fix-elements (Bolt-on 2) which
// diffs this snapshot against the latest CommitElement rows to hand
// the resolve UX a pre-populated candidate list.
//
// Design principle: hashes only, no geometry. ~60 bytes per element.
// A view of ~2000 elements produces ~120 KB — fine to ship inline
// with the create-issue payload. Server caps at 5000 entries — we
// enforce the same cap client-side so we never build a wasteful
// payload we'll get truncated anyway.
//
// === What we can and can't fill in ===
//
// The materialized-instance record (see
// webgl/js/loader/streaming/GlobalInstancePool.js acquire() and
// TileRegistry.js sidecar hydration) carries exactly these fields
// per instance: { uniqueId, hash (fragment), tileKey, disabled }.
// The per-tile sidecar schema (v6) does NOT carry paramHash,
// category, or level per instance — so we send those blank. Blank
// is a supported shape server-side (see back/routes/issue.js POST
// /issue snapshot sanitization); the diff endpoint just won't flag
// param-only changes (renames, mark edits) for these snapshots.
// Geometry changes still fire correctly off fragmentHash.

/**
 * Best-effort snapshot of what the user was looking at when they
 * created an issue.
 *
 * @param {Object} webGLRef  React ref whose .current is the WebGlApp
 *                           instance (see webgl/index.js). May be null
 *                           or lack a viewer if the 3D tab isn't loaded.
 * @returns {{
 *   sourceViewId: string,
 *   openedAtSnapshotElements: Array<{
 *     uniqueId: string,
 *     fragmentHash?: string,
 *     paramHash?: string,
 *     tileKey?: string,
 *     category?: string,
 *     level?: string,
 *   }>
 * }}
 */
export function captureOpenedAtSnapshot(webGLRef) {
  const sourceViewId = generateViewId();
  const viewer = webGLRef?.current?.viewer || null;
  if (!viewer) return { sourceViewId, openedAtSnapshotElements: [] };

  const elements = collectMaterializedInstances(viewer);
  return { sourceViewId, openedAtSnapshotElements: elements };
}

/**
 * Walk the streaming pool registry and materialize every currently-
 * pooled instance into a hashable snapshot row.
 *
 * Priority order for source of truth:
 *   1. viewer.snapshotVisibleInstances() — if a future viewer
 *      exposes a purpose-built collector, prefer that.
 *   2. viewer.streaming.poolRegistry.pools — walk the pool userData.
 *      Confirmed accessor path (see streamingLoader.js
 *      enumerateDisabledUids for the same walk pattern).
 *   3. viewer.stateStore (legacy fallback) — grouped uid sets only,
 *      no hashes. Better than nothing but produces a snapshot the
 *      diff endpoint can't fingerprint changes against.
 *
 * Instances hidden via stateStore group/isolate at snapshot time are
 * included — the "before" of an issue is about what the reporter was
 * LOOKING AT, and legitimately-hidden things (say, an isolated MEP
 * pass) are part of that context. Instances the plugin marked
 * `disabled` at export time are excluded — they never render and
 * shouldn't count as "visible."
 */
function collectMaterializedInstances(viewer) {
  const out = [];
  const seen = new Set();

  try {
    if (typeof viewer.snapshotVisibleInstances === "function") {
      const raw = viewer.snapshotVisibleInstances() || [];
      for (const r of raw) push(out, seen, r);
      return capped(out);
    }
  } catch (e) {
    console.warn("[openedAtSnapshot] viewer.snapshotVisibleInstances threw:", e?.message);
  }

  const registry = viewer.streaming?.poolRegistry
                || viewer.streaming?.registry
                || null;
  if (registry?.pools?.forEach) {
    try {
      registry.pools.forEach((arr) => {
        if (!Array.isArray(arr)) return;
        for (let lod = 0; lod < arr.length; lod++) {
          const pool = arr[lod];
          const userData = pool?.mesh?.userData;
          if (!Array.isArray(userData)) continue;
          for (let i = 0; i < userData.length; i++) {
            const inst = userData[i];
            if (!inst || !inst.uniqueId) continue;
            if (inst.disabled) continue;
            push(out, seen, {
              uniqueId: inst.uniqueId,
              fragmentHash: inst.hash || "",
              tileKey: inst.tileKey || "",
            });
            if (out.length >= 5000) return; // hard cap; server enforces same
          }
        }
      });
      if (out.length > 0) return capped(out);
    } catch (e) {
      console.warn("[openedAtSnapshot] poolRegistry walk threw:", e?.message);
    }
  }

  return out;
}

function push(out, seen, row) {
  if (!row || typeof row.uniqueId !== "string" || !row.uniqueId) return;
  if (seen.has(row.uniqueId)) return;
  seen.add(row.uniqueId);
  out.push({
    uniqueId:     row.uniqueId,
    fragmentHash: row.fragmentHash || row.hash || "",
    paramHash:    row.paramHash    || "",
    tileKey:      row.tileKey      || "",
    category:     row.category     || "",
    level:        row.level        || "",
  });
}

function capped(rows) {
  return rows.length > 5000 ? rows.slice(0, 5000) : rows;
}

// Web-friendly viewId — no dependency on uuid because the shape is
// only used for round-trip identification, not security.
function generateViewId() {
  const rnd = () => Math.random().toString(16).slice(2, 10);
  return `web-view-${Date.now().toString(16)}-${rnd()}`;
}
